/**
 * Data layer for the COMPANY panel's Trigger Trace section.
 *
 * NOTHING ANYBODY TYPED ARRIVES HERE. A trigger log carries two free-text
 * fields — `notes` and `description` — where somebody writes privately about a
 * moment that hurt. Neither is read by any endpoint behind this file. The
 * trigger name itself is also a free string: the app offers eight built-in
 * options but lets people invent their own, so the backend maps every value
 * outside its catalogue to the single label 'Something else' inside the
 * pipeline. A typed name cannot reach this file, whatever it says.
 *
 * NO TRIGGER CONTENT IS ATTACHED TO A NAME. The users fetcher below returns
 * counts and dates — how much somebody used the tool and how much of it they
 * closed — and nothing about what they logged. There is no per-person fetcher,
 * because there is no per-person endpoint, because the moment a trigger or an
 * intensity appears beside an email address this stops being a wellbeing
 * dashboard.
 *
 * SMALL COHORTS COME BACK EMPTY. Under the backend's minimum the payload is
 * `{ suppressed: true, minimum }` with no figures at all. Pages must check
 * `suppressed` before reading anything else. Inside a reported page, individual
 * cross-tab cells can still be withheld — `avgIntensity` and `resolutionRate`
 * arrive as null when too few logs sit behind them, which is why the tables
 * render a dash rather than a zero for null.
 *
 * See Backend services/triggerHubService.js for the full reasoning.
 *
 * Backed by the company/v1 trigger-hub endpoints (token-scoped to the company;
 * the B2B/Members toggle is appended by api/companyApi.js). Every fetcher takes
 * an optional `department` so the section's filter narrows the whole page.
 */

import {
  getCompanyTriggerDashboard,
  getCompanyTriggerPatterns,
  getCompanyTriggerResolution,
  getCompanyTriggerUsers
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

const suppressedShape = (data) => ({
  suppressed: true,
  minimum: data.minimum ?? 0,
  totalUsers: data.totalUsers ?? 0
});

/** The figures every tab shares — reach, closure and the distributions. */
const adaptCore = (data) => ({
  suppressed: false,
  minimum: data.minimum ?? 0,

  // Reach
  totalUsers: data.totalUsers ?? 0,
  participants: data.participants ?? 0,
  adoption: data.adoption ?? 0,
  logs: data.logs ?? 0,
  logsPerPerson: data.logsPerPerson ?? 0,

  // Closing the loop
  resolved: data.resolved ?? 0,
  failed: data.failed ?? 0,
  pending: data.pending ?? 0,
  closed: data.closed ?? 0,
  // Resolved out of what was CLOSED, not out of everything — still-open logs
  // are excluded so the people using the tool most aren't penalised for it.
  resolutionRate: data.resolutionRate ?? 0,
  closureRate: data.closureRate ?? 0,
  openRate: data.openRate ?? 0,
  avgDaysToResolve: data.avgDaysToResolve ?? null,

  // How hard
  avgIntensity: data.avgIntensity ?? null,
  // What share of logs filled in each optional field, so a thin chart can say so.
  coverage: data.coverage || { intensity: 0, lifeArea: 0, emotion: 0 },

  // Engagement
  dailyReturnRate: data.dailyReturnRate ?? 0,
  weeklyReturnRate: data.weeklyReturnRate ?? 0,
  customVocabulary: data.customVocabulary || { names: 0, people: 0, share: 0 },

  // Distributions
  statusSplit: data.statusSplit || [],
  lifeAreaSplit: data.lifeAreaSplit || [],
  triggerSplit: data.triggerSplit || [],
  emotionSplit: data.emotionSplit || [],
  durationSplit: data.durationSplit || [],
  intensitySplit: data.intensitySplit || [],
  volumeSplit: data.volumeSplit || [],
  activeDaySplit: data.activeDaySplit || [],

  // Series. Backend zero-fills the last 30 days; charts consume plain arrays.
  weekday: data.weekday || [],
  logsTrend: (data.trend || []).map((day) => day.logs || 0),
  peopleTrend: (data.trend || []).map((day) => day.people || 0)
});

/** Overview: reach, closure, and the headline work share. */
export const adaptOverview = (data) => {
  if (data?.suppressed) return suppressedShape(data);
  return {
    ...adaptCore(data),
    workShare: data.workShare ?? 0,
    topTriggers: data.topTriggers || [],
    topEmotions: data.topEmotions || []
  };
};

/** Patterns: the full mix plus the cross-tabs. */
export const adaptPatterns = (data) => {
  if (data?.suppressed) return suppressedShape(data);
  return {
    ...adaptCore(data),
    workShare: data.workShare ?? 0,
    cellMinimum: data.cellMinimum ?? 0,
    // Rows whose `reportable` is false carry null derived figures on purpose.
    areaBreakdown: data.areaBreakdown || [],
    triggerBreakdown: data.triggerBreakdown || [],
    emotionBreakdown: data.emotionBreakdown || []
  };
};

/** Resolution: the closing act. */
export const adaptResolution = (data) => {
  if (data?.suppressed) return suppressedShape(data);
  return {
    ...adaptCore(data),
    cellMinimum: data.cellMinimum ?? 0,
    timeToResolveSplit: data.timeToResolveSplit || [],
    backlogSplit: data.backlogSplit || [],
    areaBreakdown: data.areaBreakdown || [],
    resolutionByIntensity: data.resolutionByIntensity || []
  };
};

export const fetchCompanyTriggerOverview = async (department = '') => {
  try {
    return adaptOverview(unwrap(await getCompanyTriggerDashboard(department)));
  } catch (error) {
    console.error('fetchCompanyTriggerOverview error:', error);
    return null;
  }
};

export const fetchCompanyTriggerPatterns = async (department = '') => {
  try {
    return adaptPatterns(unwrap(await getCompanyTriggerPatterns(department)));
  } catch (error) {
    console.error('fetchCompanyTriggerPatterns error:', error);
    return null;
  }
};

export const fetchCompanyTriggerResolution = async (department = '') => {
  try {
    return adaptResolution(unwrap(await getCompanyTriggerResolution(department)));
  } catch (error) {
    console.error('fetchCompanyTriggerResolution error:', error);
    return null;
  }
};

/**
 * The people list. Counts and dates only — see the file header.
 * `resolutionRate` is null, not 0, for anybody who has closed nothing; those
 * are different facts and the table renders them differently.
 */
export const fetchCompanyTriggerUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const response = unwrap(
      await getCompanyTriggerUsers({ page, perPage, search, department })
    );
    return {
      data: response?.data || [],
      totalCount: response?.totalCount || 0
    };
  } catch (error) {
    console.error('fetchCompanyTriggerUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};
